import { useRef, useEffect, useState } from "react";

const exec = (cmd, val = null) => document.execCommand(cmd, false, val);

// Editor di testo formattato (HTML) in stile Outlook: barra bianca, gruppi chiari,
// pulsanti grandi con stato attivo evidenziato.
export default function RichEditor({ html, onChange, testid, minHeight = 260 }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [active, setActive] = useState({});

  useEffect(() => {
    try { document.execCommand("defaultParagraphSeparator", false, "div"); } catch (_) {}
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && (html || "") !== el.innerHTML) {
      el.innerHTML = html || "";
    }
  }, [html]);

  useEffect(() => {
    const upd = () => {
      if (!ref.current || !ref.current.contains(document.getSelection()?.anchorNode)) return;
      try {
        setActive({
          bold: document.queryCommandState("bold"),
          italic: document.queryCommandState("italic"),
          underline: document.queryCommandState("underline"),
          strikeThrough: document.queryCommandState("strikeThrough"),
          insertUnorderedList: document.queryCommandState("insertUnorderedList"),
          insertOrderedList: document.queryCommandState("insertOrderedList"),
          justifyLeft: document.queryCommandState("justifyLeft"),
          justifyCenter: document.queryCommandState("justifyCenter"),
          justifyRight: document.queryCommandState("justifyRight"),
        });
      } catch (_) {}
    };
    document.addEventListener("selectionchange", upd);
    return () => document.removeEventListener("selectionchange", upd);
  }, []);

  const emit = () => onChange(ref.current ? ref.current.innerHTML : "");
  const focusArea = () => ref.current && ref.current.focus();
  const run = (e, cmd, val) => { e.preventDefault(); focusArea(); exec(cmd, val); emit(); };

  const addLink = (e) => {
    e.preventDefault(); focusArea();
    const url = window.prompt("Indirizzo del link:", "https://");
    if (url) { exec("createLink", url); emit(); }
  };

  const insertImageDataUrl = (dataUrl) => {
    focusArea();
    exec("insertHTML", `<img src="${dataUrl}" style="max-width:100%;height:auto;border-radius:6px" />`);
    emit();
  };

  const onPickImage = (e) => {
    const f = (e.target.files || [])[0];
    if (f) {
      const r = new FileReader();
      r.onload = () => insertImageDataUrl(String(r.result));
      r.readAsDataURL(f);
    }
    e.target.value = "";
  };

  const insertTable = (e) => {
    e.preventDefault(); focusArea();
    const rows = parseInt(window.prompt("Numero di righe:", "3") || "0", 10);
    const cols = parseInt(window.prompt("Numero di colonne:", "3") || "0", 10);
    if (!rows || !cols) return;
    let html = '<table style="border-collapse:collapse;width:100%;margin:8px 0" border="1"><tbody>';
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) html += '<td style="border:1px solid #cbd5e1;padding:6px 8px;min-width:40px">&nbsp;</td>';
      html += "</tr>";
    }
    html += "</tbody></table><p><br></p>";
    exec("insertHTML", html);
    emit();
  };

  const captureScreen = async (e) => {
    e.preventDefault();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      alert("La cattura schermo non è supportata da questo browser."); return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      await new Promise((res) => setTimeout(res, 300));
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL("image/png");
      stream.getTracks().forEach((t) => t.stop());
      insertImageDataUrl(dataUrl);
    } catch (err) { /* annullato dall'utente */ }
  };

  // Incolla: se ci sono immagini negli appunti le inserisce; altrimenti mantiene
  // la formattazione HTML originale.
  const onPaste = (e) => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        if (file) {
          const r = new FileReader();
          r.onload = () => insertImageDataUrl(String(r.result));
          r.readAsDataURL(file);
        }
        return;
      }
    }
  };

  const B = ({ cmd, val, title, wide, children }) => (
    <button type="button" className={"rte-btn" + (active[cmd] ? " on" : "") + (wide ? " wide" : "")} data-tip={title}
      onMouseDown={(ev) => run(ev, cmd, val)}
      data-testid={`rte-${cmd}${val ? "-" + val.replace(/[<>]/g, "") : ""}`}>{children}</button>
  );

  return (
    <div className="rte" data-testid={testid ? `${testid}-wrap` : undefined}>
      <div className="rte-toolbar">
        <div className="rte-group">
          <B cmd="undo" title="Annulla (Ctrl+Z)">↶</B>
          <B cmd="redo" title="Ripeti (Ctrl+Y)">↷</B>
        </div>
        <span className="rte-sep" />
        <div className="rte-group">
          <select className="rte-select" data-tip="Dimensione del testo" defaultValue=""
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => { focusArea(); exec("fontSize", e.target.value); emit(); e.target.value = ""; }}
            data-testid="rte-fontsize">
            <option value="" disabled>Dimensione</option>
            <option value="2">Piccolo</option>
            <option value="3">Normale</option>
            <option value="5">Grande</option>
            <option value="6">Molto grande</option>
          </select>
        </div>
        <span className="rte-sep" />
        <div className="rte-group">
          <B cmd="bold" title="Grassetto (Ctrl+B)"><b>G</b></B>
          <B cmd="italic" title="Corsivo (Ctrl+I)"><i>C</i></B>
          <B cmd="underline" title="Sottolineato (Ctrl+U)"><u>S</u></B>
          <B cmd="strikeThrough" title="Barrato"><s>ab</s></B>
          <label className="rte-btn rte-color" data-tip="Colore del testo">
            <span className="rte-a">A</span>
            <input type="color" onChange={(e) => { focusArea(); exec("foreColor", e.target.value); emit(); }} data-testid="rte-forecolor" />
          </label>
          <label className="rte-btn rte-color rte-hilite" data-tip="Evidenziatore">
            <span className="rte-a rte-a-hl">A</span>
            <input type="color" defaultValue="#ffe600" onChange={(e) => { focusArea(); exec("hiliteColor", e.target.value) || exec("backColor", e.target.value); emit(); }} data-testid="rte-hilite" />
          </label>
        </div>
        <span className="rte-sep" />
        <div className="rte-group">
          <B cmd="insertUnorderedList" title="Elenco puntato">⋮≡</B>
          <B cmd="insertOrderedList" title="Elenco numerato">1≡</B>
          <B cmd="outdent" title="Riduci rientro">⇤</B>
          <B cmd="indent" title="Aumenta rientro">⇥</B>
        </div>
        <span className="rte-sep" />
        <div className="rte-group">
          <B cmd="justifyLeft" title="Allinea a sinistra">☰</B>
          <B cmd="justifyCenter" title="Centra">☷</B>
          <B cmd="justifyRight" title="Allinea a destra">☱</B>
        </div>
        <span className="rte-sep" />
        <div className="rte-group">
          <B cmd="formatBlock" val="<h2>" title="Titolo"><b>T</b></B>
          <B cmd="formatBlock" val="<blockquote>" title="Citazione">❝</B>
          <B cmd="insertHorizontalRule" title="Riga separatrice">—</B>
        </div>
        <span className="rte-sep" />
        <div className="rte-group">
          <button type="button" className="rte-btn" data-tip="Inserisci link" onMouseDown={addLink} data-testid="rte-link">🔗</button>
          <button type="button" className="rte-btn" data-tip="Inserisci immagine" onMouseDown={(e) => { e.preventDefault(); fileRef.current && fileRef.current.click(); }} data-testid="rte-image">🖼</button>
          <button type="button" className="rte-btn" data-tip="Inserisci tabella" onMouseDown={insertTable} data-testid="rte-table">▦</button>
          <button type="button" className="rte-btn" data-tip="Cattura schermo" onMouseDown={captureScreen} data-testid="rte-capture">📷</button>
        </div>
        <span className="rte-sep" />
        <B cmd="removeFormat" title="Cancella tutta la formattazione">🧹</B>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onPickImage} data-testid="rte-image-input" />
      </div>
      <div ref={ref} className="rte-area" contentEditable suppressContentEditableWarning
        style={{ minHeight }} onInput={emit} onBlur={emit} onPaste={onPaste} data-testid={testid} />
    </div>
  );
}
